<template>
  <div class="panel panel-bordered mb-0">
    <div class="panel-heading">
      <div class="panel-title">
        {{ isNew ? `Create Question #${questionId || ''}` : `Edit Question #${questionId}` }}
      </div>
      <div class="panel-actions panel-actions-keep">
        <span class="save-state" :class="saveStateClass">{{ saveStateText }}</span>
      </div>
    </div>

    <div class="panel-body">
      <div class="form-group row">
        <label class="form-control-label col-md-3">Question Body</label>
        <div class="col-md-9">
          <textarea
            rows="3"
            class="form-control"
            v-model.trim="localQuestion.question"
            :disabled="disabled"
            @input="onFieldInput"
            @blur="onFieldBlur"
          ></textarea>
        </div>
      </div>

      <div class="form-group row">
        <label class="form-control-label col-md-3">Choice 1</label>
        <div class="col-md-9">
          <input
            type="text"
            class="form-control"
            v-model.trim="localQuestion.choice1"
            :disabled="disabled"
            @input="onFieldInput"
            @blur="onFieldBlur"
          />
        </div>
      </div>

      <div class="form-group row">
        <label class="form-control-label col-md-3">Choice 2</label>
        <div class="col-md-9">
          <input
            type="text"
            class="form-control"
            v-model.trim="localQuestion.choice2"
            :disabled="disabled"
            @input="onFieldInput"
            @blur="onFieldBlur"
          />
        </div>
      </div>

      <div class="form-group row">
        <label class="form-control-label col-md-3">Correct Answer</label>
        <div class="col-md-9">
          <select
            class="form-control"
            v-model="localQuestion.correct_answer"
            :disabled="disabled"
            @change="onFieldBlur"
          >
            <option :value="null" disabled>Select answer</option>
            <option :value="1">1</option>
            <option :value="2">2</option>
          </select>
        </div>
      </div>

      <div class="form-group row mb-0">
        <label class="form-control-label col-md-3">Extra time</label>
        <div class="col-md-9">
          <input
            type="number"
            class="form-control"
            v-model.number="localQuestion.extra_time"
            min="0"
            max="1000"
            :disabled="disabled"
            @input="onFieldInput"
            @blur="onFieldBlur"
          />
        </div>
      </div>

      <div class="status-text mt-20">
        <span v-if="validationMessage" class="text-danger">{{ validationMessage }}</span>
        <span v-else-if="saveError" class="text-danger">{{ saveError }}</span>
        <span v-else class="text-muted">
          Changes are auto-saved when you leave each field.
        </span>
      </div>
    </div>

    <div class="panel-footer">
      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-round btn-default" @click="close">
          <i class="icon md-close" aria-hidden="true"></i>
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { gameService } from "@/api/client";

const createDefaultQuestion = () => ({
  id: null,
  question: "",
  choice1: "",
  choice2: "",
  correct_answer: null,
  extra_time: 0,
});

export default {
  name: "SpaceQuestionModal",

  props: {
    gameId: {
      type: [Number, String],
      required: true,
    },
    question: {
      type: Object,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localQuestion: createDefaultQuestion(),
      questionId: null,
      saveState: "unsaved",
      saveError: "",
      hasSavedChanges: false,
      syncing: true,
      currentSavePromise: null,
    };
  },

  computed: {
    validationMessage() {
      const { question, choice1, choice2, correct_answer, extra_time } =
        this.localQuestion;

      if (!question) return "Question Body is required before auto-save.";
      if (!choice1) return "Choice 1 is required before auto-save.";
      if (!choice2) return "Choice 2 is required before auto-save.";
      if (![1, 2].includes(Number(correct_answer))) {
        return "Correct Answer must be either 1 or 2.";
      }
      if (
        isNaN(Number(extra_time)) ||
        Number(extra_time) < 0 ||
        Number(extra_time) > 1000
      ) {
        return "Extra time must be between 0 and 1000.";
      }

      return "";
    },

    saveStateClass() {
      return {
        "is-saved": this.saveState === "saved",
        "is-saving": this.saveState === "saving",
        "is-unsaved": this.saveState === "unsaved",
      };
    },

    saveStateText() {
      if (this.saveState === "saving") return "saving";
      if (this.saveState === "saved") return "saved";
      return "unsaved";
    },
  },

  watch: {
    question: {
      immediate: true,
      handler(question) {
        this.syncing = true;
        this.localQuestion = Object.assign(createDefaultQuestion(), question || {});
        this.questionId = question ? question.id : null;
        this.saveState = question && !this.isNew ? "saved" : "unsaved";
        this.saveError = "";
        this.hasSavedChanges = false;

        this.$nextTick(() => {
          this.syncing = false;
        });
      },
    },

  },

  methods: {
    onFieldInput() {
      if (this.syncing || this.disabled) return;

      this.saveError = "";
      if (this.saveState !== "saving") {
        this.saveState = "unsaved";
      }
    },

    async onFieldBlur() {
      if (this.syncing || this.disabled) return;

      this.onFieldInput();
      await this.persistQuestion();
    },

    buildPayload() {
      return {
        question: this.localQuestion.question,
        choice1: this.localQuestion.choice1,
        choice2: this.localQuestion.choice2,
        correct_answer: Number(this.localQuestion.correct_answer),
        extra_time: Number(this.localQuestion.extra_time),
      };
    },

    async persistQuestion() {
      if (this.validationMessage || !this.questionId || this.disabled) return;
      if (this.currentSavePromise) {
        await this.currentSavePromise;
        return;
      }

      this.saveState = "saving";
      this.saveError = "";

      try {
        this.currentSavePromise = gameService.updateSpaceQuestion(
          this.gameId,
          this.questionId,
          this.buildPayload()
        );
        await this.currentSavePromise;

        this.hasSavedChanges = true;
        this.saveState = "saved";
      } catch (error) {
        this.saveState = "unsaved";
        this.saveError =
          (error.response && error.response.data) ||
          error.message ||
          "Auto save failed.";
      } finally {
        this.currentSavePromise = null;
      }
    },

    async close() {
      if (this.saveState === "saving" && this.currentSavePromise) {
        await this.currentSavePromise;
      }

      if (!this.validationMessage && this.saveState === "unsaved") {
        await this.persistQuestion();
      }

      this.$emit("close", {
        refresh: this.hasSavedChanges,
        cleanupQuestionId:
          this.isNew && !this.hasSavedChanges ? this.questionId : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.save-state {
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  text-transform: capitalize;
  font-size: 0.8rem;
  font-weight: 600;
  background: #edf1f7;
  color: #526069;

  &.is-saved {
    color: #0b6b2b;
    background: #e7f7ed;
  }

  &.is-saving {
    color: #8a5a00;
    background: #fff4d8;
  }

  &.is-unsaved {
    color: #a94442;
    background: #fde8e7;
  }
}

.status-text {
  min-height: 24px;
}
</style>
